import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Shell = ({ title, subtitle, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden flex-col justify-between overflow-hidden bg-navy p-12 text-white lg:flex grid-bg">
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-health/25 blur-[120px]" />
      <Logo light />
      <div className="relative">
        <p className="eyebrow">Safety. Simplified.</p>
        <h2 className="mt-4 max-w-md text-4xl font-bold leading-tight">Describe the job. Let Saflr do the paperwork.</h2>
        <p className="mt-4 max-w-md text-slate-400">RAMS, risk assessments, COSHH, policies and more — drafted by AI, controlled by your competent people.</p>
      </div>
      <p className="relative text-xs text-slate-500">AI drafts always require competent human review and approval.</p>
    </div>
    <div className="flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md fade-up">
        <div className="mb-8 lg:hidden"><Logo /></div>
        <h1 className="text-3xl font-bold">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Field = ({ label, id, ...p }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`${id}-input`} className="h-11" {...p} /></div>
);

const routeFor = (u) => (u.is_super_admin ? "/admin" : u.org_id ? "/app" : "/onboarding");

export const Login = () => {
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", f);
      const u = await refresh();
      setUser(u);
      nav(routeFor(u));
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <Shell title="Welcome back" subtitle="Log in to your Saflr workspace.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" id="login-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password" id="login-password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-link">Forgot password?</Link></div>
        <Button className="h-11 w-full rounded-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Log in"}</Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">New to Saflr? <Link to="/register" className="font-medium text-foreground" data-testid="register-link">Create an account</Link></p>
    </Shell>
  );
};

export const Register = () => {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the terms to continue");
    setBusy(true);
    try {
      await api.post("/auth/register", f);
      await refresh();
      nav(`/onboarding${params.get("plan") ? `?plan=${params.get("plan")}` : ""}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <Shell title="Create your account" subtitle="Start your free trial. No card required.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" id="register-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="Work email" id="register-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password (min 8 characters)" id="register-password" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <label className="flex items-start gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="register-terms" />
          <span>I agree to the <Link to="/legal/terms-of-service" className="underline">Terms</Link>, <Link to="/legal/privacy-policy" className="underline">Privacy Policy</Link> and <Link to="/legal/ai-disclaimer" className="underline">AI Content Disclaimer</Link>.</span>
        </label>
        <Button className="h-11 w-full rounded-full" disabled={busy} data-testid="register-submit">{busy ? "Creating…" : "Create account"}</Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-medium text-foreground">Log in</Link></p>
    </Shell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Shell title="Reset your password" subtitle="We'll send a reset link if an account exists.">
      {sent ? <p data-testid="forgot-sent" className="rounded-xl border bg-card p-5 text-sm">If an account exists for {email}, a reset link has been issued. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button className="h-11 w-full rounded-full" data-testid="forgot-submit">Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-muted-foreground">Back to log in</Link></p>
    </Shell>
  );
};

const TokenPassword = ({ endpoint, title, done }) => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, { token: params.get("token"), password: pw });
      toast.success(done);
      if (endpoint.includes("invite")) {
        await refresh();
        nav("/app");
      } else nav("/login");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Shell title={title}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password (min 8 characters)" id="new-password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button className="h-11 w-full rounded-full" data-testid="set-password-submit">Continue</Button>
      </form>
    </Shell>
  );
};

export const ResetPassword = () => <TokenPassword endpoint="/auth/reset-password" title="Choose a new password" done="Password updated. Please log in." />;
export const AcceptInvite = () => <TokenPassword endpoint="/auth/accept-invite" title="Join your team on Saflr" done="Welcome to Saflr" />;

export const VerifyEmail = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState("Verifying…");
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") }).then(() => setState("Your email address is verified.")).catch((e) => setState(errMsg(e)));
  }, [params]);
  return <Shell title="Email verification"><p data-testid="verify-state" className="rounded-xl border bg-card p-5 text-sm">{state}</p><Link to="/app" className="mt-6 inline-block text-sm font-medium">Continue to Saflr →</Link></Shell>;
};
